import React from 'react';
import { ProfileFormData } from './CreateProfileWizard';

interface Props {
  formData: ProfileFormData;
}

export default function ProfileLivePreview({ formData }: Props) {
  const initials = formData.displayName
    ? formData.displayName.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()
    : 'U';

  const socials = [
    formData.instagram && '📸',
    formData.youtube && '▶',
    formData.twitter && '🐦',
    formData.website && '🌐',
  ].filter(Boolean) as string[];

  return (
    <div className="sticky top-6 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-600 text-muted-foreground">Live Preview</p>
        <span className="flex items-center gap-1.5 text-xs font-600 text-success">
          <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse-soft" />
          Updates live
        </span>
      </div>

      {/* Phone frame */}
      <div className="bg-card rounded-2xl border border-border card-shadow overflow-hidden">
        {/* Browser bar */}
        <div className="bg-muted px-4 py-2.5 flex items-center gap-2 border-b border-border">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
          </div>
          <div className="flex-1 bg-background rounded-md px-3 py-1 text-xs text-muted-foreground font-mono text-center truncate">
            biolink.app/{formData.username || 'yourname'}
          </div>
        </div>

        {/* Profile */}
        <div className="bg-gradient-brand p-6 flex flex-col items-center">
          <div className="w-16 h-16 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center text-white font-800 text-xl mb-3">
            {initials}
          </div>
          <p className="text-white font-700 text-base text-center">
            {formData.displayName || 'Your Name'}
          </p>
          {formData.location && (
            <p className="text-white/60 text-xs mt-0.5">📍 {formData.location}</p>
          )}
          {formData.bio && (
            <p className="text-white/70 text-xs mt-2 text-center line-clamp-3 max-w-48">
              {formData.bio}
            </p>
          )}
          {socials.length > 0 && (
            <div className="flex gap-2 mt-3">
              {socials.map((icon, i) => (
                <span
                  key={`prev-soc-${i}`}
                  className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-sm"
                >
                  {icon}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 bg-background space-y-2">
          <div className="h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-xs font-600 text-primary">
            Your links appear here
          </div>
          <div className="h-8 rounded-xl bg-muted animate-pulse" />
          <div className="h-8 rounded-xl bg-muted animate-pulse" style={{ opacity: 0.6 }} />
          <p className="text-center text-xs text-muted-foreground pt-1">
            Made with <span className="text-primary font-600">BioLink</span>
          </p>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        This is how your page will look to visitors
      </p>
    </div>
  );
}